import React, { useEffect, useMemo, useState } from 'react';
import styles from '../../styles/AdminPanel.module.scss';
import { assetUrl } from '../../utils/imageUrl';
import { getStockLabelAdminText } from '../../utils/productStockStatus';
import { filterCategoriesHierarchical } from '../../utils/categoryTree';

const getProductCategoryId = (product) =>
  product.category?.id ?? product.categoryId ?? null;

const getCategoryStats = (categoryId, products) => {
  const items = products.filter((p) => getProductCategoryId(p) === categoryId);
  const published = items.filter((p) => p.inStock !== false).length;
  const hidden = items.filter((p) => p.inStock === false).length;
  return {
    total: items.length,
    published,
    hidden,
    deleted: 0,
    variations: items.length,
  };
};

const CategoriesList = ({
  categories,
  products,
  onEdit,
  onDelete,
  onCreate,
  onEditProduct,
  onDeleteProduct,
  onCreateProduct,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);

  const totalProducts = products.length;

  const categoriesWithStats = useMemo(() => {
    return categories.map((category) => ({
      ...category,
      stats: getCategoryStats(category.id, products),
    }));
  }, [categories, products]);

  const filteredCategories = useMemo(() => {
    const statsById = new Map(categoriesWithStats.map((category) => [category.id, category]));
    return filterCategoriesHierarchical(categoriesWithStats, categorySearch).map((category) => ({
      ...(statsById.get(category.id) || category),
      depth: category.depth,
    }));
  }, [categoriesWithStats, categorySearch]);

  useEffect(() => {
    if (!filteredCategories.length) {
      setSelectedId(null);
      return;
    }
    const stillVisible = filteredCategories.some((c) => c.id === selectedId);
    if (!selectedId || !stillVisible) {
      setSelectedId(filteredCategories[0].id);
    }
  }, [filteredCategories, selectedId]);

  const selectedCategory = filteredCategories.find((c) => c.id === selectedId) ?? null;

  const categoryProducts = useMemo(() => {
    if (!selectedId) return [];
    const q = productSearch.trim().toLowerCase();
    return products
      .filter((p) => getProductCategoryId(p) === selectedId)
      .filter((p) => {
        if (!q) return true;
        return [p.title, p.article, String(p.id)].some((v) =>
          String(v || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => String(a.title).localeCompare(String(b.title), 'uk'));
  }, [products, selectedId, productSearch]);

  const allCategoriesSelected =
    filteredCategories.length > 0 && selectedCategories.length === filteredCategories.length;

  const toggleAllCategories = () => {
    if (allCategoriesSelected) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(filteredCategories.map((c) => c.id));
    }
  };

  const toggleCategorySelect = (id) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  if (!categories.length) {
    return (
      <div className={styles.categoriesManager}>
        <div className={styles.categoriesManagerEmpty}>
          <p>Категорії отсутствуют</p>
          <button type="button" className={styles.categoriesAddBtn} onClick={onCreate}>
            Створити категорію
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.categoriesManager}>
      <div className={styles.categoriesManagerLayout}>
        {/* Левая панель — Групи */}
        <aside className={styles.categoriesPanel}>
          <div className={styles.categoriesPanelHeader}>
            <h3>Групи</h3>
            <button type="button" className={styles.categoriesAddBtnSmall} onClick={onCreate}>
              +
            </button>
          </div>
          <div className={styles.categoriesPanelSearch}>
            <input
              type="search"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Пошук за групами"
            />
          </div>
          <div className={styles.categoriesTableHead}>
            <span className={styles.categoriesTableCheck}>
              <input type="checkbox" checked={allCategoriesSelected} onChange={toggleAllCategories} />
            </span>
            <span className={styles.categoriesTableHeadName}>
              Назва групи
              <small>{categories.length}</small>
            </span>
            <span className={styles.categoriesTableHeadCount}>
              Позиції
              <small>{totalProducts}</small>
            </span>
          </div>
          <div className={styles.categoriesList}>
            {filteredCategories.map((category) => {
              const isActive = category.id === selectedId;
              return (
                <div
                  key={category.id}
                  className={`${styles.categoryRow} ${isActive ? styles.categoryRowActive : ''}`}
                >
                  <span className={styles.categoriesTableCheck}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category.id)}
                      onChange={() => toggleCategorySelect(category.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                  <button
                    type="button"
                    className={styles.categoryRowMain}
                    onClick={() => setSelectedId(category.id)}
                  >
                    <div className={styles.categoryRowThumb}>
                      <img src={assetUrl(category.image)} alt={category.name} />
                    </div>
                    <div className={styles.categoryRowInfo}>
                      <span
                        className={`${styles.categoryRowTitle} ${category.depth > 0 ? styles.categoryRowTitleChild : ''}`}
                        style={category.depth > 0 ? { paddingLeft: `${category.depth * 18}px` } : undefined}
                      >
                        {category.name}
                      </span>
                      <div className={styles.categoryRowStats}>
                        {category.isPublished === false && (
                          <span className={styles.productsTableTag}>Прихована</span>
                        )}
                        <span>Усього: <b>{category.stats.total}</b></span>
                        <span>Опубл.: <b>{category.stats.published} / {category.stats.total}</b></span>
                        <span>Приховані: <b>{category.stats.hidden}</b></span>
                        <span>Видалені: <b>{category.stats.deleted}</b></span>
                        <span>Різновид.: <b>{category.stats.variations}</b></span>
                      </div>
                    </div>
                    <span className={styles.categoryRowCount}>{category.stats.total}</span>
                  </button>
                  <div className={styles.categoryRowActions}>
                    <div className={styles.actionsMenu}>
                      <button
                        type="button"
                        className={styles.actionsMenuBtn}
                        onClick={() => setOpenMenuId(openMenuId === category.id ? null : category.id)}
                      >
                        Дії ▾
                      </button>
                      {openMenuId === category.id && (
                        <div className={styles.actionsMenuList}>
                          <button type="button" onClick={() => { onEdit(category); setOpenMenuId(null); }}>
                            Редагувати
                          </button>
                          <button type="button" onClick={() => { onCreateProduct(category.id); setOpenMenuId(null); }}>
                            Додати товар
                          </button>
                          <button type="button" onClick={() => { onDelete(category); setOpenMenuId(null); }}>
                            Видалити
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Правая панель — Позиції */}
        <section className={styles.positionsPanel}>
          <div className={styles.positionsPanelHeader}>
            <h3>Позиції</h3>
            {selectedCategory && (
              <button
                type="button"
                className={styles.categoriesAddBtnSmall}
                onClick={() => onCreateProduct(selectedCategory.id)}
                title="Додати товар у категорію"
              >
                +
              </button>
            )}
          </div>
          <div className={styles.categoriesPanelSearch}>
            <input
              type="search"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Пошук за товарами"
              disabled={!selectedCategory}
            />
          </div>

          {!selectedCategory ? (
            <div className={styles.positionsEmpty}>Оберіть категорію зліва</div>
          ) : categoryProducts.length === 0 ? (
            <div className={styles.positionsEmpty}>
              <p>В категорії «{selectedCategory.name}» немає товарів</p>
              <button
                type="button"
                className={styles.categoriesAddBtn}
                onClick={() => onCreateProduct(selectedCategory.id)}
              >
                Додати товар
              </button>
            </div>
          ) : (
            <div className={styles.positionsList}>
              {categoryProducts.map((product) => (
                <div key={product.id} className={styles.positionRow}>
                  <div className={styles.positionDrag} title="Порядок">⠿</div>
                  <div className={styles.positionThumb}>
                    <img src={assetUrl(product.images?.[0] || '')} alt={product.title} />
                  </div>
                  <button
                    type="button"
                    className={styles.positionTitle}
                    onClick={() => onEditProduct(product)}
                  >
                    {product.title}
                  </button>
                  <div className={styles.positionStatus}>
                    <span className={product.inStock !== false ? styles.stockIn : styles.stockOut}>
                      {getStockLabelAdminText(product.stockLabel, product.inStock)}
                    </span>
                  </div>
                  <div className={styles.positionPrice}>{product.price} ₴</div>
                  <div className={styles.positionActions}>
                    <button
                      type="button"
                      className={styles.positionActionBtn}
                      onClick={() => onCreateProduct(selectedCategory.id)}
                      title="Додати схожий"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className={`${styles.positionActionBtn} ${styles.positionActionDelete}`}
                      onClick={() => onDeleteProduct(product)}
                      title="Видалити"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CategoriesList;
